import React from "react";
import PermitHeader from "./PermitHeader";
import useCoordinationPermitForm from "../../../hooks/useCoordinationPermitForm";
import LoadingSpinner from "../../Common/LoadingSpinner";
import { CodeStatus, PermitCategory, PermitType } from "../../../common/enums";
import PermitActionBar from "./PermitActionBar";
import DateTimeInput from "../../Common/DateTimeInput";
import TimeInput from "../../Common/TimeInput";
import RemindMe from "./RemindMe";
import TextInput from "../../Common/TextInput";
import RadioButton from "../../Common/RadioButton";
import LabelCheckbox from "../../Common/LabelCheckbox";
import TextArea from "../../Common/TextArea";
import SignatureDisplay from "../../Signatures/SignatureDisplay";
import SignatureModal from "../../Signatures/SignatureModal";
import Button from "../../Common/Button";
import RejectPermitModal from "../RejectPermitModal";
import DangerAlert from "../../Common/DangerAlert";
import { PermitFormProps } from "../../../types/forms/Permits/PermitFormProps";
import PositionPicker from "./PositionPicker";

function CoordinationPermitForm(props: PermitFormProps) {
    const formManager = useCoordinationPermitForm(props);

    if (formManager.isLoading) {
        return <LoadingSpinner />;
    }

    const { resources } = props;
    const { formState, storedPermit, department, formStateChangeHandler, registerFormStateChange } = formManager;

    const submitHandler = formManager.canCreate
        ? formManager.submitCreateHandlerAsync
        : formManager.canUpdate
        ? formManager.submitUpdateHandlerAsync
        : undefined;

    return (
        <>
            <div className="mt-5 mb-5">
                <div className="container">
                    <div className="mb-5">
                        <PermitHeader
                            formState={formState}
                            permitDocument={storedPermit}
                            permitNameResource={resources.get("CoordinationPermit")}
                            permitType={PermitType.CoordinationPermit}
                            resources={resources}
                        />
                    </div>

                    {storedPermit && storedPermit.status === CodeStatus.Rejected && storedPermit.rejectionComment ? (
                        <DangerAlert>{storedPermit.rejectionComment}</DangerAlert>
                    ) : null}

                    {storedPermit ? (
                        <PermitActionBar
                            acceptPermitClickHandler={formManager.acceptButtonClickHandler}
                            copyPermitClickHandler={formManager.copyButtonClickHandler}
                            rejectPermitClickHandler={formManager.rejectPermitClickHandler}
                            department={department}
                            permitDocument={storedPermit}
                            resources={props.resources}
                            requestInProgress={formManager.requestInProgress}
                        />
                    ) : null}
                    <form onSubmit={submitHandler} onKeyDown={formManager.formKeyDownHandler} className="mt-4">
                        <PositionPicker
                            canManagePositions={formManager.canManagePositions}
                            canManagePositionIsActive={formManager.canManagePositionActiveStatus}
                            canManagePolygons={formManager.canManagePositionPolygons}
                            canManageComments={formManager.canManagePositions}
                            onPositionAdded={formManager.addPositionPickerRowHandler}
                            onPositionChange={formManager.updatePositionPickerRowHandler}
                            onPositionRemoved={formManager.removePositionPickerRowHandler}
                            onUpdatePositionsClicked={formManager.updatePositionsClickHandlerAsync}
                            positions={formState.positions}
                            resources={props.resources}
                            showUpdatePositionsButton={!!formManager.showUpdatePositionsButton}
                            canManageCategories={false}
                            selectedCategories={[]}
                            showActiveStatus={true}
                            showCategories={false}
                        />

                        <div className="d-flex mt-3">
                            <span className="fw-bold me-2">{resources.get("TypeOfWork")}:</span>

                            <RadioButton
                                checked={formState.permitCategory.value === PermitCategory.HotWorkCategoryOne}
                                inputId="type-of-work__hot-work-category-one"
                                label={resources.get("HotWorkCategoryOne")}
                                onSelect={() =>
                                    formStateChangeHandler("permitCategory", PermitCategory.HotWorkCategoryOne)
                                }
                                containerClassName="me-3"
                                name="type-of-work"
                                readOnly={!formManager.canManageContent}
                            />
                            <RadioButton
                                checked={formState.permitCategory.value === PermitCategory.HotWorkCategoryTwo}
                                inputId="type-of-work__hot-work-category-two"
                                label={resources.get("HotWorkCategoryTwo")}
                                onSelect={() =>
                                    formStateChangeHandler("permitCategory", PermitCategory.HotWorkCategoryTwo)
                                }
                                name="type-of-work"
                                containerClassName="me-3"
                                readOnly={!formManager.canManageContent}
                            />
                            <RadioButton
                                checked={
                                    formState.permitCategory.value === PermitCategory.WorkWithRiskOfFlammableLiquidOrGas
                                }
                                inputId="type-of-work__work-with-risk-of-flammable-liquid-or-gas"
                                label={resources.get("WorkInvolvingRiskOfFlammableLiquidOrGas")}
                                onSelect={() =>
                                    formStateChangeHandler(
                                        "permitCategory",
                                        PermitCategory.WorkWithRiskOfFlammableLiquidOrGas
                                    )
                                }
                                name="type-of-work"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <DateTimeInput
                                defaultValue={formState.startDate.value ?? new Date()}
                                id="start-date"
                                label={resources.get("StartDateTime")}
                                onBlur={formManager.startDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.startDate.errorMessage}
                                ref={formManager.startDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                            <TimeInput
                                defaultValue={formState.endDate.value ?? new Date()}
                                inputId="end-date"
                                label={resources.get("EndTime")}
                                onBlur={formManager.endTimeHandler.blurHandler}
                                containerClassName="col-md-6"
                                errorMessage={formState.endDate.errorMessage}
                                min={formState.startDate.value}
                                ref={formManager.endTimeHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.referenceNr.value}
                                errorMessage={formState.referenceNr.errorMessage}
                                inputId="reference-nr"
                                labelText={props.resources.get("ReferenceNr")}
                                onChange={(s) => formStateChangeHandler("referenceNr", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextArea
                                errorMessage={formState.description.errorMessage}
                                value={formState.description.value}
                                labelText={props.resources.get("WorkPurposeDescription")}
                                onChange={(s) => formStateChangeHandler("description", s)}
                                textAreaId="description"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.company.value}
                                errorMessage={formState.company.errorMessage}
                                inputId="company"
                                labelText={props.resources.get("Company")}
                                onChange={(s) => formStateChangeHandler("company", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.companyContactPerson.name}
                                errorMessage={formState.companyContactPerson.nameErrorMessage}
                                inputId="company-contact-person__name"
                                labelText={props.resources.get("CompanyContact")}
                                onChange={(s) =>
                                    registerFormStateChange((prevValue) => ({
                                        ...prevValue,
                                        companyContactPerson: {
                                            ...prevValue.companyContactPerson,
                                            name: s
                                        }
                                    }))
                                }
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.companyContactPerson.phoneNumber}
                                errorMessage={formState.companyContactPerson.phoneNumberErrorMessage}
                                inputId="company-contact-person__phone-number"
                                labelText={props.resources.get("CompanyContactPhoneNumber")}
                                onChange={(s) =>
                                    registerFormStateChange((prevValue) => ({
                                        ...prevValue,
                                        companyContactPerson: {
                                            ...prevValue.companyContactPerson,
                                            phoneNumber: s
                                        }
                                    }))
                                }
                                containerClassName="col-md-6"
                                placeHolder="e.g. +46705555555"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.companyContactPersonEmail.value}
                                errorMessage={formState.companyContactPersonEmail.errorMessage}
                                inputId="company-contact-person-email"
                                labelText={props.resources.get("CompanyContactEmail")}
                                onChange={(s) => formStateChangeHandler("companyContactPersonEmail", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.entrepreneur.value}
                                errorMessage={formState.entrepreneur.errorMessage}
                                inputId="entrepreneur"
                                labelText={props.resources.get("Entrepreneur")}
                                onChange={(s) => formStateChangeHandler("entrepreneur", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.entrepreneurContactPerson.name}
                                errorMessage={formState.entrepreneurContactPerson.nameErrorMessage}
                                inputId="entrepreneur-contact-person__name"
                                labelText={props.resources.get("EntrepreneurContact")}
                                onChange={(s) =>
                                    registerFormStateChange((prevValue) => ({
                                        ...prevValue,
                                        entrepreneurContactPerson: {
                                            ...prevValue.entrepreneurContactPerson,
                                            name: s
                                        }
                                    }))
                                }
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.entrepreneurContactPerson.phoneNumber}
                                errorMessage={formState.entrepreneurContactPerson.phoneNumberErrorMessage}
                                inputId="entrepreneur-contact-person__phone-number"
                                labelText={props.resources.get("EntrepreneurContactPhoneNumber")}
                                onChange={(s) =>
                                    registerFormStateChange((prevValue) => ({
                                        ...prevValue,
                                        entrepreneurContactPerson: {
                                            ...prevValue.entrepreneurContactPerson,
                                            phoneNumber: s
                                        }
                                    }))
                                }
                                containerClassName="col-md-6"
                                placeHolder="e.g. +46705555555"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.entrepreneurContactPersonEmail.value}
                                errorMessage={formState.entrepreneurContactPersonEmail.errorMessage}
                                inputId="entrepreneur-contact-person-email"
                                labelText={props.resources.get("EntrepreneurContactEmail")}
                                onChange={(s) => formStateChangeHandler("entrepreneurContactPersonEmail", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div
                            className="mt-3 p-3 rounded"
                            style={{
                                backgroundColor: "#FFF9EA"
                            }}
                        >
                            <h1 className="h4">{resources.get("CoordinationInformation")}</h1>
                            <LabelCheckbox
                                checked={formState.workCloseToNeighbours.value}
                                id="work-close-to-neighbours"
                                label={resources.get("WorkCloseToNeighbours")}
                                onChange={(checked) => formStateChangeHandler("workCloseToNeighbours", checked)}
                                readOnly={!formManager.canManageContent}
                            />

                            <div className="row">
                                <TextArea
                                    value={formState.ghabInformsCompanies.value}
                                    errorMessage={formState.ghabInformsCompanies.errorMessage}
                                    labelText={resources.get("GHABInformsNeighbours")}
                                    onChange={(s) => formStateChangeHandler("ghabInformsCompanies", s)}
                                    textAreaId="ghab-informs-neighbours"
                                    containerClassName="col-md-6"
                                    readOnly={!formManager.canManageContent}
                                />
                                <TextArea
                                    value={formState.senderInformsCompanies.value}
                                    errorMessage={formState.senderInformsCompanies.errorMessage}
                                    labelText={resources.get("WeInformNeighbours")}
                                    onChange={(s) => formStateChangeHandler("senderInformsCompanies", s)}
                                    textAreaId="sender-informs-neighbours"
                                    containerClassName="col-md-6"
                                    readOnly={!formManager.canManageContent}
                                />
                            </div>
                            <LabelCheckbox
                                containerClassName="mt-3 mb-3"
                                checked={formState.informPerformer.value}
                                id="inform-performer"
                                label={resources.get("InformPerformer")}
                                onChange={(checked) => formStateChangeHandler("informPerformer", checked)}
                                labelClassName="fw-bold"
                                readOnly={!formManager.canManageContent}
                            />

                            <ul className="mt-3">
                                <li>{resources.get("CancelWorkOnAccident")}</li>
                                <li>{resources.get("NewPermitOnTimeChange")}</li>
                                <li>
                                    {resources.get("OnHotWorkNearNeighbour1")}{" "}
                                    <u> {resources.get("OnHotWorkNearNeighbour2")}</u>.
                                </li>
                                <li>{resources.get("ContactCoordinator")}</li>
                            </ul>
                        </div>

                        <SignatureDisplay
                            resources={resources}
                            signatureManager={formManager.permitProviderSignatureManager}
                            signatureLabel={resources.get("PermitProvider")}
                            containerClassName="mt-3 mb-4"
                        />

                        {formManager.canCreate ? (
                            <Button className="mt-3" type="submit" disabled={formManager.requestInProgress}>
                                {resources.get("Send")}
                            </Button>
                        ) : formManager.canUpdate ? (
                            <Button className="mt-3" type="submit" disabled={formManager.requestInProgress}>
                                {resources.get("Update")}
                            </Button>
                        ) : null}
                    </form>
                </div>
            </div>
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.permitProviderSignatureManager}
                ref={formManager.permitProviderSignatureManager.signaturePadRef}
            />

            {storedPermit ? (
                <RejectPermitModal
                    onHide={() => formManager.setShowRejectPermitModal(false)}
                    onReject={formManager.rejectPermitHandlerAsync}
                    resources={resources}
                    show={formManager.showRejectPermitModal}
                />
            ) : null}
        </>
    );
}

export default CoordinationPermitForm;
